import { getAccessToken } from "../lib/auth";
import { parseApiError } from "../lib/api-error";

const API_BASE = "/api";

// =============================================================================
// Types
// =============================================================================

export interface KeycloakUser {
  id: string;
  username: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  emailVerified: boolean;
  enabled: boolean;
  createdTimestamp: number;
  attributes?: Record<string, string[]>;
  requiredActions?: string[];
  totp: boolean;
  federatedIdentities?: Array<{
    identityProvider: string;
    userId: string;
    userName: string;
  }>;
}

export interface KeycloakGroup {
  id: string;
  name: string;
  path: string;
  subGroups?: KeycloakGroup[];
}

export interface KeycloakRole {
  id: string;
  name: string;
  description?: string;
  composite: boolean;
  clientRole: boolean;
}

export interface UserSession {
  id: string;
  userId: string;
  ipAddress: string;
  start: number;
  lastAccess: number;
  clients: Record<string, string>;
}

export interface UsersListResponse {
  users: KeycloakUser[];
  total: number;
}

// =============================================================================
// Fetch helper
// =============================================================================

async function fetchUsersAPI<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const token = await getAccessToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options?.headers as Record<string, string>),
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const response = await fetch(`${API_BASE}${endpoint}`, { ...options, headers });

  if (!response.ok) {
    throw await parseApiError(response);
  }

  return response.json();
}

// =============================================================================
// API
// =============================================================================

export const usersApi = {
  // Users
  list: (params?: { search?: string; first?: number; max?: number; enabled?: boolean }, signal?: AbortSignal) => {
    const qs = new URLSearchParams();
    if (params?.search) qs.set("search", params.search);
    if (params?.first !== undefined) qs.set("first", String(params.first));
    if (params?.max !== undefined) qs.set("max", String(params.max));
    if (params?.enabled !== undefined) qs.set("enabled", String(params.enabled));
    const query = qs.toString();
    return fetchUsersAPI<UsersListResponse>(`/users${query ? `?${query}` : ""}`, { signal });
  },

  get: (id: string) => fetchUsersAPI<KeycloakUser>(`/users/${id}`),

  create: (data: {
    username: string;
    email: string;
    firstName?: string;
    lastName?: string;
    enabled?: boolean;
    password?: string;
    temporaryPassword?: boolean;
  }) =>
    fetchUsersAPI<{ id: string }>("/users", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  update: (id: string, data: Partial<Pick<KeycloakUser, "username" | "email" | "firstName" | "lastName" | "enabled" | "emailVerified" | "attributes">>) =>
    fetchUsersAPI<{ ok: boolean }>(`/users/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    fetchUsersAPI<{ ok: boolean }>(`/users/${id}`, { method: "DELETE" }),

  setEnabled: (id: string, enabled: boolean) =>
    fetchUsersAPI<{ ok: boolean }>(`/users/${id}/enabled`, {
      method: "PATCH",
      body: JSON.stringify({ enabled }),
    }),

  resetPassword: (id: string, password: string, temporary = true) =>
    fetchUsersAPI<{ ok: boolean }>(`/users/${id}/reset-password`, {
      method: "POST",
      body: JSON.stringify({ password, temporary }),
    }),

  setRequiredActions: (id: string, actions: string[]) =>
    fetchUsersAPI<{ ok: boolean }>(`/users/${id}/required-actions`, {
      method: "POST",
      body: JSON.stringify({ actions }),
    }),

  // Groups
  listGroups: () => fetchUsersAPI<{ groups: KeycloakGroup[] }>("/groups"),

  getUserGroups: (id: string) =>
    fetchUsersAPI<{ groups: KeycloakGroup[] }>(`/users/${id}/groups`),

  addUserToGroup: (userId: string, groupId: string) =>
    fetchUsersAPI<{ ok: boolean }>(`/users/${userId}/groups/${groupId}`, { method: "PUT" }),

  removeUserFromGroup: (userId: string, groupId: string) =>
    fetchUsersAPI<{ ok: boolean }>(`/users/${userId}/groups/${groupId}`, { method: "DELETE" }),

  // Roles
  listRoles: () => fetchUsersAPI<{ roles: KeycloakRole[] }>("/roles"),

  getUserRoles: (id: string) =>
    fetchUsersAPI<{ roles: KeycloakRole[] }>(`/users/${id}/roles`),

  addUserRoles: (userId: string, roles: Array<{ id: string; name: string }>) =>
    fetchUsersAPI<{ ok: boolean }>(`/users/${userId}/roles`, {
      method: "POST",
      body: JSON.stringify({ roles }),
    }),

  removeUserRoles: (userId: string, roles: Array<{ id: string; name: string }>) =>
    fetchUsersAPI<{ ok: boolean }>(`/users/${userId}/roles`, {
      method: "DELETE",
      body: JSON.stringify({ roles }),
    }),

  // Sessions
  getUserSessions: (id: string) =>
    fetchUsersAPI<{ sessions: UserSession[] }>(`/users/${id}/sessions`),

  logoutAll: (id: string) =>
    fetchUsersAPI<{ ok: boolean }>(`/users/${id}/sessions`, { method: "DELETE" }),

  logoutSession: (userId: string, sessionId: string) =>
    fetchUsersAPI<{ ok: boolean }>(`/users/${userId}/sessions/${sessionId}`, { method: "DELETE" }),
};
